import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { LeadService } from 'src/app/private/services/lead.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';

@Component({
    selector: 'app-find-duplicate-leads',
    standalone: true,
    imports: [CommonModule, DialogModule, ButtonModule, TableModule, ProgressSpinnerModule, TooltipModule],
    templateUrl: './find-duplicate-leads.component.html',
    styleUrl: './find-duplicate-leads.component.scss'
})
export class FindDuplicateLeadsComponent implements OnInit {
    @Input() visible: boolean = false;
    @Output() visibleChange = new EventEmitter<boolean>();
    @Output() reload = new EventEmitter<void>();

    loading: boolean = false;
    duplicateData: any[] = [];
    displayedDuplicates: any[] = [];
    totalDuplicateGroups: number = 0;
    totalLeadsToDelete: number = 0;

    // Pagination
    currentPage: number = 1;
    pageSize: number = 10;
    totalPages: number = 0;

    // Expanded rows state
    expandedRows: { [key: string]: boolean } = {};

    // Expose Math for template
    Math = Math;
    isAdminUser: boolean = false;
    deletingAll: boolean = false;
    deletingGroup: { [key: string]: boolean } = {};

    constructor(
        private api: LeadService,
        private messageService: MessageService,
        private localStorage: LocalStorageService
    ) {

        this.isAdminUser = this.localStorage.isAdminUser();

    }

    ngOnInit() {
        if (this.visible) {
            this.loadDuplicates();
        }
    }

    ngOnChanges() {
        if (this.visible) {
            this.loadDuplicates();
        }
    }

    loadDuplicates() {
        this.loading = true;
        const params :any = {
            organization_id: this.localStorage.getOrganizationId(),
            campus_id: this.localStorage.getCampusId()
        };

        if(!this.isAdminUser) {
            params['user_id'] = this.localStorage.getUserId();
        }

        this.api.findDuplicateWithCount(params).subscribe({
            next: (response: any) => {
                this.duplicateData = response.duplicates || [];
                this.totalDuplicateGroups = response.total_duplicate_groups || 0;
                this.totalLeadsToDelete = response.total_leads_to_delete || 0;
                this.totalPages = Math.ceil(this.duplicateData.length / this.pageSize);
                this.updateDisplayedDuplicates();
                this.loading = false;
            },
            error: (error: any) => {
                console.error('Error loading duplicates:', error);
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to load duplicate leads'
                });
                this.loading = false;
            }
        });
    }

    updateDisplayedDuplicates() {
        const startIndex = (this.currentPage - 1) * this.pageSize;
        const endIndex = startIndex + this.pageSize;
        this.displayedDuplicates = this.duplicateData.slice(startIndex, endIndex);
    }

    onPageChange(page: number) {
        this.currentPage = page;
        this.updateDisplayedDuplicates();
    }

    nextPage() {
        if (this.currentPage < this.totalPages) {
            this.currentPage++;
            this.updateDisplayedDuplicates();
        }
    }

    previousPage() {
        if (this.currentPage > 1) {
            this.currentPage--;
            this.updateDisplayedDuplicates();
        }
    }

    toggleRow(index: string) {
        this.expandedRows[index] = !this.expandedRows[index];
    }

    isRowExpanded(index: string): boolean {
        return this.expandedRows[index] || false;
    }

    formatDate(dateString: string): string {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    }

    getFullName(lead: any): string {
        return [lead?.first_name, lead?.last_name]
            .filter(name => !!name)
            .map(name => name.charAt(0).toUpperCase() + name.slice(1).toLowerCase())
            .join(' ') || '-';
    }

    closeDialog() {
        this.visible = false;
        this.visibleChange.emit(this.visible);
        this.expandedRows = {};
    }

    getPaginationArray(): number[] {
        return Array.from({ length: this.totalPages }, (_, i) => i + 1);
    }

    deleteAllDuplicates() {
        this.deletingAll = true;
        const params: any = {
            organization_id: this.localStorage.getOrganizationId(),
            campus_id: this.localStorage.getCampusId()
        };

        if (!this.isAdminUser) {
            params['user_id'] = this.localStorage.getUserId();
        }

        this.api.deleteAllDuplicates(params).subscribe({
            next: (response: any) => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'All duplicate leads deleted successfully'
                });
                this.deletingAll = false;
                this.loadDuplicates();
                this.reload.emit();
            },
            error: (error: any) => {
                console.error('Error deleting all duplicates:', error);
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to delete duplicate leads'
                });
                this.deletingAll = false;
            }
        });
    }

    deleteDuplicateGroup(group: any) {
        const key = `${group.email}_${group.phone}`;
        this.deletingGroup[key] = true;
        
        const params: any = {
            email: group.email,
            phone: group.phone,
            organization_id: this.localStorage.getOrganizationId()
        };

        if (!this.isAdminUser) {
            params['user_id'] = this.localStorage.getUserId();
        }

        this.api.deleteDuplicateByEmailPhone(params).subscribe({
            next: (response: any) => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Duplicate group deleted successfully'
                });
                this.deletingGroup[key] = false;
                this.loadDuplicates();
                this.reload.emit();
            },
            error: (error: any) => {
                console.error('Error deleting duplicate group:', error);
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to delete duplicate group'
                });
                this.deletingGroup[key] = false;
            }
        });
    }

    isGroupDeleting(group: any): boolean {
        const key = `${group.email}_${group.phone}`;
        return this.deletingGroup[key] || false;
    }
}
